import { db, delay } from "./api";

export const salesService = {
  async getSalesOrders() {
    await delay(200);
    return [...db.salesOrders];
  },

  async getCustomers() {
    await delay(200);
    return [...db.customers];
  },

  async createCustomer(customerData) {
    await delay(300);
    const newCustomer = {
      ...customerData,
      id: customerData.type === "Wholesale" ? `CUST-W${String(db.customers.length + 1).padStart(2, "0")}` : `CUST-R${String(db.customers.length + 1).padStart(2, "0")}`,
      outstandingBalance: 0,
      totalSalesYTD: 0,
      rating: "New Customer"
    };
    db.customers.unshift(newCustomer);
    db.persistAll();
    return newCustomer;
  },

  async updateCustomer(id, updates) {
    await delay(250);
    const idx = db.customers.findIndex((c) => c.id === id);
    if (idx !== -1) {
      db.customers[idx] = { ...db.customers[idx], ...updates };
      db.persistAll();
      return db.customers[idx];
    }
    throw new Error("Customer not found");
  },

  async deleteCustomer(id) {
    await delay(250);
    const idx = db.customers.findIndex((c) => c.id === id);
    if (idx !== -1) {
      db.customers.splice(idx, 1);
      db.persistAll();
      return true;
    }
    return false;
  },

  async processSale(saleData) {
    await delay(400);

    // Deduct stock from available batches (FIFO basis)
    saleData.items.forEach((item) => {
      let needed = Number(item.qty);
      for (const batch of db.batches) {
        if (batch.productId === item.productId && batch.qtyRemaining > 0 && batch.status !== "Expired") {
          const deduction = Math.min(batch.qtyRemaining, needed);
          batch.qtyRemaining -= deduction;
          needed -= deduction;
          if (needed <= 0) break;
        }
      }
    });

    const newOrder = {
      ...saleData,
      id: `SO-${Date.now()}`,
      orderNumber: saleData.channel === "Retail" ? `POS-2026-${Math.floor(1000 + Math.random() * 9000)}` : `SO/2026/08/${Math.floor(500 + Math.random() * 500)}`,
      date: new Date().toISOString(),
      status: "Completed"
    };

    db.salesOrders.unshift(newOrder);

    // Update customer stats & balance if credit
    const customer = db.customers.find((c) => c.id === saleData.customerId);
    if (customer) {
      customer.totalSalesYTD = (customer.totalSalesYTD || 0) + Number(saleData.grandTotal);
      if (saleData.paymentStatus === "Pending" || saleData.paymentMethod.includes("Credit") || saleData.paymentMethod.includes("Ledger")) {
        customer.outstandingBalance = (customer.outstandingBalance || 0) + Number(saleData.grandTotal);
      }
    }

    // If paid immediately, log payment receipt
    if (saleData.paymentStatus === "Paid") {
      db.payments.unshift({
        id: `PAY-${Date.now()}`,
        type: "Customer Receipt",
        referenceId: newOrder.id,
        referenceNumber: newOrder.orderNumber,
        partyName: saleData.customerName || customer?.name || "Retail Walk-in",
        amount: saleData.grandTotal,
        paymentMethod: saleData.paymentMethod,
        date: new Date().toISOString().slice(0, 10),
        status: "Completed",
        notes: `Instant POS / ${saleData.channel} settlement`
      });
    }

    // Audit log
    db.auditLogs.unshift({
      id: `AUD-${Date.now()}`,
      user: saleData.cashierName || "Sales Executive",
      role: saleData.channel === "Retail" ? "Retail Shop Staff" : "Sales Team",
      action: `Completed ${saleData.channel} Sale`,
      module: "Sales",
      timestamp: new Date().toISOString().replace("T", " ").slice(0, 19),
      details: `${newOrder.orderNumber} for ₹${Number(saleData.grandTotal).toLocaleString("en-IN")} (${saleData.items.length} items, profit: ₹${saleData.totalProfit})`
    });

    db.persistAll();
    return newOrder;
  }
};
